import React, { useEffect, useRef, useState } from "react";
import { Camera, KeyRound, Eye, EyeOff, Loader2, ArrowLeft, BadgeCheck, AlertTriangle } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { COUNTRIES, PRESET_AVATARS, avatarDataUrl, findCountry, flagEmoji } from "@/lib/countries";
import PhoneField from "@/components/PhoneField";
import { getInitials, avatarColor } from "@/lib/avatar";
import { toast } from "sonner";
import { uploadAvatar } from "@/lib/uploads";

function parseInitialPhone(user) {
  const byName = COUNTRIES.find((c) => c.name === user.country);
  const stored = user.phone || "";
  // Match the longest dial code prefix
  const match = [...COUNTRIES].sort((a, b) => b.dial.length - a.dial.length).find((c) => stored.startsWith(c.dial));
  const code = (byName || match || COUNTRIES.find((c) => c.code === "US")).code;
  const national = match ? stored.slice(match.dial.length).replace(/\D/g, "") : stored.replace(/\D/g, "");
  return { code, phone: national };
}

export default function ProfileSheet({ onClose }) {
  const { user, setUser, logout } = useAuth();
  const { t } = useI18n();
  const [form, setForm] = useState({ name: user.name || "", about: user.about || "", avatar: user.avatar || null });
  const [phone, setPhone] = useState(() => parseInitialPhone(user));
  const [saving, setSaving] = useState(false);
  const [pw, setPw] = useState({ open: false, current_password: "", new_password: "", show: false });
  const [del, setDel] = useState({ open: false, busy: false });
  const fileRef = useRef(null);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const onAvatar = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    try { const url = await uploadAvatar(f); setForm((s) => ({ ...s, avatar: url })); }
    catch { toast.error("Could not upload photo"); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const country = findCountry(phone.code);
      const payload = {
        name: form.name, about: form.about, avatar: form.avatar, flag: country?.flag || flagEmoji(phone.code),
        country: country?.name, phone: phone.phone ? `${country.dial}${phone.phone}` : null,
      };
      const { data } = await api.put("/auth/profile", payload);
      setUser(data);
      toast.success(t("save") + " ✓");
      onClose();
    } catch (e) { toast.error("Error"); } finally { setSaving(false); }
  };

  const changePw = async () => {
    if (!pw.current_password || !pw.new_password) { toast.error(t("pw_fill_both")); return; }
    if (pw.new_password.length < 6) { toast.error(t("pw_too_short")); return; }
    setPw((s) => ({ ...s, busy: true }));
    try {
      await api.post("/auth/change-password", { current_password: pw.current_password, new_password: pw.new_password });
      toast.success(t("pw_updated"));
      setPw({ open: false, current_password: "", new_password: "", show: false, busy: false });
    } catch (e) {
      toast.error(e.response?.data?.detail || t("pw_change_failed"));
      setPw((s) => ({ ...s, busy: false }));
    }
  };

  const field = "w-full bg-[var(--rc-surface)] border border-[var(--rc-border)] rounded-2xl px-4 py-3.5 focus:outline-none focus:border-[var(--rc-gold)]/60";

  return (
    <div className="fixed inset-0 z-[95] flex flex-col bg-[var(--rc-bg)] text-[var(--rc-text)] rc-pop" data-testid="profile-sheet">
      <div className="flex items-center gap-3 px-4 py-4 border-b border-[var(--rc-border)] bg-[var(--rc-bg)]/90 backdrop-blur-xl">
        <button onClick={onClose} className="h-9 w-9 grid place-items-center rounded-full text-[var(--rc-silver)] hover:text-[var(--rc-gold)] hover:bg-[var(--rc-surface)]" data-testid="profile-cancel-btn">
          <ArrowLeft size={22} />
        </button>
        <h2 className="font-heading text-2xl">{t("edit_profile")}</h2>
      </div>

      <div className="flex-1 overflow-y-auto no-scrollbar">
        <div className="max-w-lg mx-auto w-full p-6 rc-safe-bottom">
          {/* avatar */}
          <div className="flex justify-center mb-5">
            <button onClick={() => fileRef.current?.click()} className="relative" data-testid="profile-avatar-btn">
              <div className="h-24 w-24 rounded-full grid place-items-center border-2 border-[var(--rc-gold)] text-3xl font-heading text-[var(--rc-gold)] overflow-hidden" style={{ backgroundColor: form.avatar ? "var(--rc-surface)" : avatarColor(form.name) }}>
                {form.avatar ? <img src={form.avatar} alt="" className="h-full w-full object-cover" /> : getInitials(form.name)}
              </div>
              <span className="absolute bottom-0 right-0 h-8 w-8 grid place-items-center rounded-full bg-[var(--rc-gold)] text-black"><Camera size={16} /></span>
            </button>
            <input ref={fileRef} type="file" accept="image/*" hidden onChange={onAvatar} />
          </div>

          {/* preset avatars */}
          <p className="text-sm text-[var(--rc-muted)] mb-3">{t("or_pick_avatar")}</p>
          <div className="grid grid-cols-6 gap-3 mb-6">
            {PRESET_AVATARS.map((p) => (
              <button key={p.id} onClick={() => setForm((s) => ({ ...s, avatar: avatarDataUrl(p) }))} data-testid={`preset-${p.id}`}
                className="aspect-square rounded-full grid place-items-center text-2xl border-2" style={{ background: p.bg, borderColor: p.ring }}>
                {p.icon}
              </button>
            ))}
          </div>

          <label className="text-sm text-[var(--rc-muted)] flex items-center gap-1.5">
            {t("name")}
            {user.email_verified && <span className="inline-flex items-center gap-1 text-[var(--rc-gold)] font-semibold" data-testid="profilesheet-verified-badge"><BadgeCheck size={14} /> {t("verified")}</span>}
          </label>
          <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={`${field} mt-1 mb-4`} data-testid="profile-name-input" />

          <label className="text-sm text-[var(--rc-muted)]">{t("about")}</label>
          <input value={form.about} onChange={(e) => setForm({ ...form, about: e.target.value })} className={`${field} mt-1 mb-4`} data-testid="profile-about-input" />

          <label className="text-sm text-[var(--rc-muted)]">{t("phone")}</label>
          <div className="mt-1 mb-6">
            <PhoneField value={phone} onChange={setPhone} />
          </div>

          <button onClick={save} disabled={saving} className="w-full flex items-center justify-center gap-2 rounded-full bg-[var(--rc-gold)] text-black font-semibold py-4 text-lg mb-3" data-testid="profile-save-btn">
            {saving && <Loader2 size={18} className="animate-spin" />} {t("save")}
          </button>

          {!pw.open ? (
            <button onClick={() => setPw({ ...pw, open: true })} className="w-full flex items-center justify-center gap-2 rounded-full border border-[var(--rc-gold)]/40 text-[var(--rc-gold)] py-3.5" data-testid="profile-changepw-btn">
              <KeyRound size={16} /> {t("change_password")}
            </button>
          ) : (
            <div className="space-y-2 rounded-2xl border border-[var(--rc-border)] p-3">
              <p className="flex items-center gap-2 text-sm font-semibold text-[var(--rc-gold)] px-1"><KeyRound size={15} /> {t("change_password")}</p>
              <div className="relative">
                <input type={pw.show ? "text" : "password"} value={pw.current_password} onChange={(e) => setPw({ ...pw, current_password: e.target.value })} placeholder={t("pw_current")} className={field} data-testid="profile-current-pw" />
                <button onClick={() => setPw({ ...pw, show: !pw.show })} className="absolute right-4 top-1/2 -translate-y-1/2 text-[var(--rc-muted)]">{pw.show ? <EyeOff size={16} /> : <Eye size={16} />}</button>
              </div>
              <input type={pw.show ? "text" : "password"} value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} placeholder={t("pw_new")} className={field} data-testid="profile-new-pw" />
              <div className="flex gap-2 pt-1">
                <button onClick={() => setPw({ open: false, current_password: "", new_password: "", show: false })} className="flex-1 rounded-full border border-[var(--rc-border)] text-[var(--rc-muted)] font-medium py-2.5" data-testid="profile-cancelpw-btn">{t("cancel")}</button>
                <button onClick={changePw} disabled={pw.busy} className="flex-1 flex items-center justify-center gap-2 rounded-full bg-[var(--rc-gold)] text-black font-semibold py-2.5 disabled:opacity-60" data-testid="profile-savepw-btn">
                  {pw.busy && <Loader2 size={15} className="animate-spin" />} {t("pw_update")}
                </button>
              </div>
            </div>
          )}

          {/* Danger zone — subtle text link, intentionally out of the natural tap flow */}
          <div className="mt-8 pt-5 border-t border-[var(--rc-border)] text-center">
            <button onClick={() => setDel({ open: true, busy: false })} data-testid="profile-delete-account-link"
              className="text-sm text-[var(--rc-muted)] hover:text-red-400 underline underline-offset-4 transition-colors">
              {t("del_account_cta")}
            </button>
          </div>
        </div>
      </div>

      {del.open && (
        <div className="fixed inset-0 z-[96] flex items-center justify-center bg-black/60 p-5" onClick={() => !del.busy && setDel({ open: false, busy: false })} data-testid="delete-account-confirm">
          <div onClick={(e) => e.stopPropagation()} className="w-full max-w-sm rounded-2xl bg-[var(--rc-surface)] border border-[var(--rc-border)] p-5 rc-pop">
            <div className="flex items-center gap-3 mb-2">
              <span className="h-10 w-10 grid place-items-center rounded-full bg-red-500/15 text-red-400 shrink-0"><AlertTriangle size={20} /></span>
              <div className="font-heading text-xl">{t("del_account_title")}</div>
            </div>
            <p className="text-sm text-[var(--rc-muted)] mb-5">{t("del_account_desc")}</p>
            <div className="flex gap-3">
              <button onClick={() => setDel({ open: false, busy: false })} disabled={del.busy} data-testid="delete-account-cancel"
                className="flex-1 rounded-full border border-[var(--rc-border)] py-2.5 font-medium text-[var(--rc-text)] disabled:opacity-60">{t("cancel")}</button>
              <button disabled={del.busy} data-testid="delete-account-confirm-btn"
                onClick={async () => {
                  setDel((s) => ({ ...s, busy: true }));
                  try {
                    await api.delete("/auth/account");
                    logout();
                    window.location.href = "/login";
                  } catch (e) {
                    toast.error(e?.response?.data?.detail || "Could not delete account");
                    setDel({ open: false, busy: false });
                  }
                }}
                className="flex-1 rounded-full bg-red-500 text-white py-2.5 font-semibold disabled:opacity-60">{del.busy ? "…" : t("del_account_cta")}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
